import { RefreshCw, ScanSearch, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  type AppRegistrationOrphan,
  type AppRegistrationReconciliationRow,
  useAppRegistrationReconciliation,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";

/**
 * Admin-only check of the desk's records against the Entra directory.
 *
 * Report only, on purpose: both lists describe something a person has to
 * judge — an application missing from Entra may have been deleted on purpose,
 * and a tagged application with no record may be one being provisioned right
 * now. Nothing here deletes or changes anything. Run on demand, because every
 * run reads the live directory.
 */
export function ReconciliationPanel() {
  const [requested, setRequested] = useState(false);
  const report = useAppRegistrationReconciliation(requested);

  const run = () => {
    if (requested) {
      void report.refetch();
    } else {
      setRequested(true);
    }
  };

  return (
    <div className="bg-card border-border mt-6 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-foreground flex items-center gap-2 text-sm font-medium">
            <ScanSearch className="size-4" />
            Directory check
          </h3>
          <p className="text-muted-foreground mt-1 text-xs">
            Compares the registrations Scaffold UI recorded with the applications in Entra. It only
            reports &mdash; nothing is changed or deleted.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={report.isFetching}>
          <RefreshCw className={report.isFetching ? "animate-spin" : undefined} />
          {report.isFetching ? "Checking…" : requested ? "Check again" : "Check against Entra"}
        </Button>
      </div>

      {report.isError && (
        <p className="text-destructive mt-3 flex items-start gap-2 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {readApiErrorMessage(report.error, "The directory could not be read.")}
        </p>
      )}

      {report.data && !report.isFetching && (
        <div className="mt-4 space-y-4">
          <p className="text-muted-foreground text-xs">
            {report.data.checked} recorded registration{report.data.checked === 1 ? "" : "s"}{" "}
            checked.
          </p>

          {report.data.complete === false && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
              <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                Incomplete: the directory listing did not finish
              </p>
              <ul className="mt-1 ml-5 list-disc text-xs text-amber-800 dark:text-amber-300">
                {(report.data.warnings ?? []).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                Applications without a record may be missing from the list below.
              </p>
            </div>
          )}

          <MissingList rows={report.data.missing_in_entra ?? []} />
          <OrphanList rows={report.data.orphaned_in_entra ?? []} />
        </div>
      )}
    </div>
  );
}

function MissingList({ rows }: { rows: AppRegistrationReconciliationRow[] }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs tracking-wider uppercase">
        Recorded, but not in Entra ({rows.length})
      </p>
      {rows.length === 0 ? (
        <p className="text-muted-foreground mt-1 text-sm">None.</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map((row) => (
            <li key={row.request_id}>
              <span className="font-mono">{row.display_name}</span>{" "}
              <span className="text-muted-foreground text-xs">
                {row.project_name} · {row.environment ?? "no environment"} · {row.status} · appId{" "}
                {row.entra_app_id}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrphanList({ rows }: { rows: AppRegistrationOrphan[] }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs tracking-wider uppercase">
        In Entra, but not recorded ({rows.length})
      </p>
      {rows.length === 0 ? (
        <p className="text-muted-foreground mt-1 text-sm">None.</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map((row) => (
            <li key={row.entra_object_id ?? row.entra_app_id ?? row.display_name}>
              <span className="font-mono">{row.display_name}</span>{" "}
              <span className="text-muted-foreground text-xs">
                appId {row.entra_app_id}
                {row.instance_tag
                  ? ` · ${row.instance_tag}`
                  : " · no instance tag (may belong to another Scaffold UI instance)"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
